import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Button, Card, CardBody, Eyebrow, IconTile, PageHeader, Pill } from '@/shared/components';
import { I } from '@/shared/icons';
import { toApiError, useApi, type ChannexConnectionItem } from '@/shared/api';
import { QuarantinedRevisionsCard } from '@/features/booking-quarantine';

const connectionsKey = ['channex', 'connections'] as const;

export function IntegrationsPage() {
  const api = useApi();
  const queryClient = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: connectionsKey,
    queryFn: () => api.channex.listConnections(),
    refetchOnWindowFocus: true,
  });

  const connect = useMutation({
    mutationFn: (channel: string) => api.channex.createConnectUrl(channel),
    onSuccess: (session) => {
      const popup = window.open(session.iframeUrl, '_blank', 'width=900,height=700');
      if (!popup) {
        toast.error('Popup wurde vom Browser blockiert — bitte erlauben und nochmal versuchen.');
      } else {
        const interval = setInterval(() => {
          if (popup.closed) {
            clearInterval(interval);
            void queryClient.invalidateQueries({ queryKey: connectionsKey });
            toast.info('Connect-Fenster geschlossen — aktualisiere Verbindungen.');
          }
        }, 1000);
      }
    },
    onError: async (err) => {
      const e = await toApiError(err);
      toast.error(e.detail ?? e.title);
    },
  });

  return (
    <>
      <PageHeader
        eyebrow="Integrationen"
        title="Konten der"
        titleAccent="Buchungsportale"
        description="Kontoweite Verbindungen zu den Buchungsportalen. Ein Portal kann mehrere Verbindungen haben — bei Booking.com etwa eine je Hotel-ID. Welches Apartment zu welcher Hotel-ID gehört, wird am Apartment festgelegt."
      />

      {error && (
        <p
          role="alert"
          className="border-sealwax/40 bg-sealwax/10 text-ink mb-4 rounded-lg border p-4 text-sm"
        >
          Die Verbindungen konnten nicht geladen werden. Bitte später erneut versuchen — besteht das
          Problem fort, ist die Anbindung an den Kanalmanager gestört.
        </p>
      )}

      {isLoading ? (
        <p className="text-ink-soft text-sm">Verbindungen werden geladen…</p>
      ) : (
        <div className="grid gap-4 md:grid-cols-2">
          {(data?.items ?? []).map((item) => (
            <OtaCard
              key={item.channel}
              item={item}
              onConnect={() => connect.mutate(item.channel)}
              busy={connect.isPending && connect.variables === item.channel}
            />
          ))}
        </div>
      )}

      <QuarantinedRevisionsCard embedded />
    </>
  );
}

interface OtaCardProps {
  item: ChannexConnectionItem;
  onConnect: () => void;
  busy: boolean;
}

function OtaCard({ item, onConnect, busy }: OtaCardProps) {
  const isOauth = item.connectMode === 'oauth';
  return (
    <Card>
      <CardBody className="p-6">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex min-w-0 items-start gap-3">
            <IconTile variant={item.connectedCount > 0 ? 'accent' : 'muted'} size="md">
              <I name="Webhook" size={18} aria-hidden />
            </IconTile>
            <div className="min-w-0">
              <h3 className="text-ink font-serif text-lg">{item.displayName}</h3>
              <p className="text-ink-soft mt-1 text-xs">
                {isOauth ? 'Eine Anmeldung gilt für das ganze Konto' : 'Je Apartment eine Hotel-ID'}
              </p>
            </div>
          </div>
          <Pill variant={item.connectedCount > 0 ? 'accent' : 'outline'} size="sm">
            {item.connectedCount > 0 ? `${item.connectedCount} verbunden` : 'nicht verbunden'}
          </Pill>
        </div>

        {item.connections.length > 0 && (
          <ul className="border-line mt-4 space-y-2 border-t pt-4">
            {item.connections.map((c) => (
              <li key={c.id} className="flex items-center justify-between gap-3 text-xs">
                <span className="text-ink-soft min-w-0 truncate font-mono">
                  {c.title || c.id.slice(0, 8)}
                </span>
                <Pill variant={c.isActive ? 'accent' : 'warning'} size="sm">
                  {c.isActive ? 'Aktiv' : 'Noch nicht bestätigt'}
                </Pill>
              </li>
            ))}
          </ul>
        )}

        <div className="mt-6 flex justify-end">
          <Button
            variant={item.connectedCount > 0 ? 'ghost' : 'brand'}
            size="sm"
            loading={busy}
            leftIcon={<I name="Webhook" size={12} aria-hidden />}
            onClick={onConnect}
          >
            {isOauth ? 'Neue Verbindung' : 'Hotel-ID hinzufügen'}
          </Button>
        </div>

        {!isOauth && (
          <Eyebrow variant="sub" className="text-ink-faint mt-3">
            Hotel-ID-Verbindungen werden direkt am Apartment gepflegt.
          </Eyebrow>
        )}
      </CardBody>
    </Card>
  );
}
